﻿import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';

import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  IconButton,
  TableHead,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import { useSelector } from 'react-redux';
import NoData from '../../../../components/NoData';
import { CustomTest } from '../../../../model/active/customTest';
import { setSelectedTestDate } from '../../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../../redux/configureStore';
import { activeTestData } from '../../../../redux/selectors/activeSelectors';
import { activeTestService } from '../../../../services/activeTestService';
import {
  getComparator,
  Order,
  StyledTableCell,
} from '../../TableComponents/TableComponents';

interface HeadCell {
  id: keyof CustomTest;
  label: string;
  numeric: boolean;
  align: 'left' | 'right' | 'inherit' | 'center' | 'justify' | undefined;
  toolTip?: string;
}

interface CustomTestTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof CustomTest
  ) => void;
  order: Order;
  orderBy: string;
  onDownloadAll: () => void;
}

function CustomTestTableHead(props: CustomTestTableProps) {
  const { order, orderBy, onRequestSort, onDownloadAll } = props;
  const { t } = useTranslation(['tests', 'common']);
  const createSortHandler =
    (property: keyof CustomTest) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      label: t('list.custom.name'),
      align: 'left',
    },
    {
      id: 'mask',
      numeric: false,
      label: t('list.custom.mask'),
      align: 'left',
    },
    {
      id: 'dateTime',
      numeric: false,
      label: t('list.custom.time'),
      align: 'left',
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          return (
            <StyledTableCell
              key={headCell.id}
              align={headCell.align}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <Tooltip title={headCell.toolTip || ''}>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? t('common:table.sortedDescending')
                        : t('common:table.sortedAscending')}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            </StyledTableCell>
          );
        })}
        <StyledTableCell align="left">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {t('list.custom.download')}
            <Tooltip title={t('list.custom.downloadAll')}>
              <IconButton
                sx={{
                  color: 'white',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                }}
                size="small"
                onClick={onDownloadAll}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

const CustomTestTableComponent: React.FC = () => {
  const { t } = useTranslation('tests');
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { patientId } = useParams();
  const data = useSelector(activeTestData);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof CustomTest>('name');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof CustomTest
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleDownloadAll = async () => {
    for (const test of data) {
      await activeTestService.DownloadActiveTestDataFile(
        test.testGuid,
        '_CTEST',
        test.name
      );
    }
  };

  const handleClick = (testDateTime: string, testGuid: string) => {
    dispatch(setSelectedTestDate(testDateTime));
    navigate(`/dashboard/patients/${patientId}/active/_CTEST/${testGuid}`, {
      state: { testDateTime },
    });
  };

  return data.length ? (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <CustomTestTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onDownloadAll={handleDownloadAll}
            />
            <TableBody>
              {data
                .slice()
                .sort(getComparator(order, orderBy))
                .map((test: CustomTest) => {
                  return (
                    <TableRow hover tabIndex={-1} key={test.id}>
                      <StyledTableCell
                        align="left"
                        onClick={() =>
                          handleClick(test.dateTime, test.testGuid)
                        }
                      >
                        {test.customName}
                      </StyledTableCell>
                      <StyledTableCell
                        align="left"
                        onClick={() =>
                          handleClick(test.dateTime, test.testGuid)
                        }
                      >
                        {test.mask}
                      </StyledTableCell>
                      <StyledTableCell
                        align="left"
                        onClick={() =>
                          handleClick(test.dateTime, test.testGuid)
                        }
                      >
                        {new Date(test.dateTime).toLocaleTimeString()}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <Box sx={{ ml: 2 }}>
                          <Tooltip title={t('list.custom.downloadFile', { name: test.name })}>
                            <IconButton
                              sx={{
                                color: 'white',
                                backgroundColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                },
                              }}
                              size="small"
                              onClick={async (event) => {
                                event.stopPropagation();
                                await activeTestService.DownloadActiveTestDataFile(
                                  test.testGuid,
                                  '_CTEST',
                                  test.name
                                );
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  ) : (
    <NoData message={t('list.noData')} />
  );
};

export default CustomTestTableComponent;
